import React from 'react';
import { motion } from 'framer-motion';
import {
  Mail, Phone, MapPin, Clock
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const Contact = () => {
  const { language } = useLanguage();

  const texts = {
    tr: {
      title: 'İletişim',
      subtitle: 'Sizinle tanışmak isteriz',
      description: 'Projeniz hakkında konuşmak veya sorularınızı yanıtlamak için buradayız. Formu doldurun, en kısa sürede size dönüş yapalım.',
      formTitle: 'Bize Yazın',
      info: {
        title: 'İletişim Bilgileri',
        email: 'info@cplus.com.tr',
        phone: '+1 (302) 417-2720',
        address: '520 Robinson LN\nUnit 4C-15 4616\nWilmington, DE 19805 US',
        hours: 'Pazartesi - Cuma: 09:00 - 18:00'
      }
    },
    en: {
      title: 'Contact',
      subtitle: 'We would love to hear from you',
      description: "We're here to discuss your project or answer your questions. Fill out the form and we'll get back to you as soon as possible.",
      formTitle: 'Write to Us',
      info: {
        title: 'Contact Information',
        email: 'info@cplus.com.tr',
        phone: '+1 (302) 417-2720',
        address: '520 Robinson LN\nUnit 4C-15 4616\nWilmington, DE 19805 US',
        hours: 'Monday - Friday: 09:00 - 18:00'
      }
    }
  };

  const t = texts[language] || texts.tr;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={language === 'tr' ? 'İletişim - Bize Ulaşın' : 'Contact Us - Get in Touch'}
        description={language === 'tr' ? 'Projeleriniz için teklif almak veya tanışmak için bizimle iletişime geçin. Sizleri ağırlamaktan mutluluk duyarız.' : 'Contact us for a quote or to meet. We would be happy to host you.'}
        keywords="iletişim, teklif al, contact, drivebundle, media agency"
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-green-600 via-teal-600 to-cyan-600 py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/90 max-w-3xl mx-auto mb-4"
          >
            {t.subtitle}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/70 max-w-2xl mx-auto"
          >
            {t.description}
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-1 space-y-6"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t.info.title}
            </h2>

            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">E-posta</p>
                  <a href={`mailto:${t.info.email}`} className="text-gray-900 dark:text-white font-medium hover:text-blue-600 dark:hover:text-blue-400">
                    {t.info.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{language === 'tr' ? 'Telefon' : 'Phone'}</p>
                  <a href={`tel:${t.info.phone.replace(/\s/g, '')}`} className="text-gray-900 dark:text-white font-medium hover:text-blue-600 dark:hover:text-blue-400">
                    {t.info.phone}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{language === 'tr' ? 'Adres' : 'Address'}</p>
                  <p className="text-gray-900 dark:text-white font-medium whitespace-pre-line">
                    {t.info.address}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{language === 'tr' ? 'Çalışma Saatleri' : 'Working Hours'}</p>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {t.info.hours}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8">
              <ContactForm title={t.formTitle} />
            </div>
          </motion.div>
        </div>

        {/* Map Placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-12"
        >
          <div className="bg-gray-200 dark:bg-gray-800 rounded-2xl h-80 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {language === 'tr' ? 'Harita yakında eklenecek' : 'Map coming soon'}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
